import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';

import styles from './RankList.module.less';
import SoftCardRank from '../../components/SoftCardRank/SoftCardRank';
import { setIframeVisible } from '../../actions/layout';
import {
  logNative,
  getUrlByTargetContent,
  getSoftType,
  sortSoftsByLocalSoftInit,
} from '../../utils/utils';
import { CONTEXT } from '../../config';
import { logging, logPostInfo } from '../../actions/log';
import { useInView } from 'react-intersection-observer';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectLocalSoftsInit = createSelector(
  state => state.native,
  native => native.installedSoftInit,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);
  const localSoftsInit = useSelector(selectLocalSoftsInit);

  const LOG_ACTION_SOURCE = {
    c_more: `${logInfo.clTag || logInfo.tag}_Group_More`,
    source: `${logInfo.tag}_Group`,
  };

  const [showData, setShowData] = useState(null);
  const [showSofts, setShowSofts] = useState([]);

  const [ref, inView] = useInView({
    threshold: 1,
  });
  const [refDom, inViewDom] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (inView && logInfo && logInfo.changView) {
      logInfo.changView({
        pageCardId: data && data.dataList && data.dataList[0] && data.dataList[0].pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
        cardType: data && data.cardType,
        org: data && data.dataList && data.dataList[0] && data.dataList[0].org,
      });
    }
  }, [inView]);

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (data && data.dataList && data.dataList.length > 0 && data.dataList[0].apps) {
      // 目前只支持行数配置后显示。列数固定为3列。
      const pageSize = parseInt(data.row, 10) * 6;
      const showSoftArr = sortSoftsByLocalSoftInit(data.dataList[0].apps || [], localSoftsInit);
      // 可展示应用个数小于配置的需要展示应用数，则只显示一行即为3个，如果配置应用数小于3个则不展示该group
      if (showSoftArr.length < 3) {
        setShowSofts([]);
      } else {
        setShowSofts(showSoftArr.slice(0, parseInt(data.row, 10) * 6));
      }
      setShowData(data.dataList[0]);
    }
  }, [dispatch, localSoftsInit, data]);

  const handleShowMore = useCallback(
    bannerInfo => {
      // 推荐页面banner点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_more,
          label: '',
          param2: {
            source: `${LOG_ACTION_SOURCE.source}||${showData.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: showData.pageCardContentId,
            name: showData.name,
          },
          param3: bannerInfo.bizInfo,
        }),
      );
      if (
        bannerInfo.targetType === '1' ||
        bannerInfo.targetType === '3' ||
        bannerInfo.targetType === '11'
      ) {
        dispatch(
          logPostInfo({
            source_p: `${LOG_ACTION_SOURCE.source}||${showData.pageCardContentId}`,
            source_pp: logInfoData.source_p,
          }),
        );
        let url = `${CONTEXT}/`;
        if (bannerInfo.targetType && bannerInfo.targetContent) {
          url = getUrlByTargetContent(bannerInfo);
        }
        history.push(url);
      } else if (bannerInfo.targetType === '2') {
        if (bannerInfo.targetUrl) {
          window.open(bannerInfo.targetUrl);
        } else if (bannerInfo.targetContent) {
          try {
            const targetContent = JSON.parse(bannerInfo.targetContent) || {};
            window.open(targetContent.targetUrl);
          } catch (error) {}
        }
      } else if (bannerInfo.targetType === '5' && bannerInfo.targetContent) {
        let targetContent = {};
        try {
          targetContent = JSON.parse(bannerInfo.targetContent);
        } catch (error) {
          logNative(`分析商城URL JSON解析错误:${error}`, error);
        }
        if (targetContent && targetContent.url) {
          dispatch(
            setIframeVisible({
              visible: true,
              url: targetContent.url,
              source: LOG_ACTION_SOURCE.source,
            }),
          );
        }
      } else {
        history.push(`${CONTEXT}/error`);
      }
    },
    [dispatch, logInfoData, history, showData],
  );

  let realRow = parseInt(showSofts.length / 6);
  if (realRow < 1) {
    realRow = 1;
  }
  return showSofts && showSofts.length > 0 ? (
    <div
      className={inViewDom ? styles.card_cardIn : styles.card_cardOut}
      ref={ref}
      data-item={data.cardType}
    >
      <div className={styles.card} ref={refDom} style={{ height: `${28 + realRow * 130}px` }}>
        {inViewDom && (
          <>
            <div className={styles.card_header}>
              <div className={styles.card_header_title}>{data.title}</div>
              {data.operate === '2' && (
                <div className={styles.card_header_more} onClick={() => handleShowMore(data)}>
                  更多
                </div>
              )}
            </div>
            <div className={styles.content_column3} style={{ height: `${realRow * 130}px` }}>
              {showSofts.map((item, j) => (
                <React.Fragment key={j}>
                  <SoftCardRank
                    soft={{ ...item, cardShowDesc: true }}
                    logInfo={{
                      ...logInfo,
                      ///////////////////////////
                      softid: item?.androidType === '1' ? item?.packageName : item?.softID,
                      softType: getSoftType(item),
                      contentType: 'soft',
                      rankCode: showData.code,
                      org: showData.org || '',
                      cardType: data.cardType,
                      cardName: data.cardType,
                      pageCardTitle: data.title,
                      pageCardId: showData.pageCardContentId,
                      ///////////////////////////
                      source: `${LOG_ACTION_SOURCE.source}||${showData.pageCardContentId}`,
                      source_p: logInfoData.source_p,
                      source_pp: logInfoData.source_pp,
                    }}
                    key={j}
                  />
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  ) : (
    ''
  );
});
